import React from "react";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { API_get_onboarding_summary } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import { handleOnboardingAction } from "./handleOnboardingAction";


export default function OnboardingNextStepCard(props) {

  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  const refreshSummary = React.useCallback(async () => {
    if (!props.homeCompany) {
      return;
    }
    setLoading(true);
    try {
      const response = await getRightworkAPI(API_get_onboarding_summary, {
        company_id: props.homeCompany,
      });
      setSummary(response?.data?.redux?.payload || null);
    } catch (error) {
      console.error("Unable to load onboarding summary", error);
    } finally {
      setLoading(false);
    }
  }, [props.homeCompany]);

  React.useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  const currentStoreSummary = React.useMemo(() => {
    if (!summary?.storeSummaries?.length) {
      return null;
    }
    return (
      summary.storeSummaries.find((store) => Number(store.store_id) === Number(props.homeStore)) ||
      summary.storeSummaries[0]
    );
  }, [summary, props.homeStore]);

  const nextAction = summary?.nextBestAction || currentStoreSummary?.nextBestAction;
  const actionStoreSummary = React.useMemo(() => {
    if (nextAction?.scope !== "store" || !nextAction.store_id) {
      return null;
    }
    return summary?.storeSummaries?.find(
      (store) => Number(store.store_id) === Number(nextAction.store_id)
    ) || null;
  }, [nextAction, summary]);

  const companyProgressContext = summary
    ? {
        dismissalKey: `company-${props.homeCompany}`,
        progressPercent: summary.companyProgressPercent,
        subject: summary.company?.company_name || "Company",
        nextBestAction: summary.nextBestAction,
        isCompanyScope: true,
      }
    : null;
  const progressContext = nextAction?.scope === "store"
    ? actionStoreSummary || currentStoreSummary || companyProgressContext
    : companyProgressContext || currentStoreSummary;
  const dismissalKey = progressContext?.store_id || progressContext?.dismissalKey;

  React.useEffect(() => {
    if (!dismissalKey) {
      return;
    }
    setDismissed(
      window.sessionStorage.getItem(
        `onboarding-next-step-dismissed-${dismissalKey}`
      ) === "true"
    );
  }, [dismissalKey]);

  if (!progressContext || progressContext.progressPercent >= 100 || dismissed) {
    return null;
  }

  const showProgress = !progressContext.isCompanyScope || progressContext.progressPercent > 0;
  const primaryLabel = nextAction?.primaryAction?.label || "Open onboarding";
  const dismissCard = () => {
    window.sessionStorage.setItem(
      `onboarding-next-step-dismissed-${dismissalKey}`,
      "true"
    );
    setDismissed(true);
  };

  return (
    <div className="fixed bottom-5 right-5 z-[1100] w-[calc(100vw-2rem)] max-w-sm rounded-xl border border-violet-200 bg-white/95 p-5 shadow-2xl backdrop-blur">
      <button
        onClick={dismissCard}
        className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        aria-label="Dismiss onboarding reminder"
      >
        <CloseRoundedIcon fontSize="small" />
      </button>
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-4 pr-8">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-700 text-white shadow-sm">
            <ChecklistRoundedIcon />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-700">
              Onboarding
            </p>
            <h3 className="mt-1 text-lg text-slate-900">
              {progressContext.isCompanyScope
                ? progressContext.progressPercent > 0
                  ? `${progressContext.subject} setup is ${progressContext.progressPercent}% complete`
                  : `${progressContext.subject} setup is underway`
                : `${progressContext.store_name} is ${progressContext.progressPercent}% set up`}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {nextAction?.description || "Finish the remaining setup steps to make this location self-service ready."}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {showProgress && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Progress</p>
              <div className="mt-2 h-2 rounded-full bg-slate-200">
                <div
                  className="h-2 rounded-full bg-violet-700 transition-all"
                  style={{ width: `${progressContext.progressPercent}%` }}
                />
              </div>
            </div>
          )}
          <div className="mt-1 flex flex-wrap gap-2">
            <button
              onClick={() => handleOnboardingAction(nextAction?.primaryAction || { tab: "onboardingCenter" }, {
                companyId: props.homeCompany,
                homeStore: props.homeStore,
                changeHomeStore: props.change_home_store,
                setCurrentTab: props.setCurrentTab,
                refreshSummary,
              })}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-700 px-4 py-2 text-sm font-medium text-white hover:bg-violet-800"
              disabled={loading}
            >
              {primaryLabel}
              <ArrowForwardRoundedIcon fontSize="small" />
            </button>
            <button
              onClick={() => props.setCurrentTab("onboardingCenter")}
              className="rounded-lg border border-violet-200 px-4 py-2 text-sm text-violet-700 hover:bg-violet-50"
            >
              View plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
